import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const LEGAL_DOCUMENTS = {
  privacy: {
    file: "privacy-policy.md",
    title: "Privacy Policy",
  },
  terms: {
    file: "terms-of-service.md",
    title: "Terms of Service",
  },
  "subscription-terms": {
    file: "subscription-terms.md",
    title: "Subscription Terms",
  },
  dmca: {
    file: "dmca-policy.md",
    title: "DMCA Policy",
  },
};

const LegalPage = ({ slug }) => {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const doc = LEGAL_DOCUMENTS[slug];

  useEffect(() => {
    if (!doc) {
      setLoading(false);
      setError("Document not found");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/legal/${doc.file}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load (${res.status})`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) {
          setContent(text);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [doc]);

  if (!doc) {
    return (
      <div className="min-h-screen bg-forest-950 text-forest-100 flex flex-col items-center justify-center p-8">
        <h1 className="text-3xl font-bold text-gold-400 mb-4">Not found</h1>
        <a href="/" className="text-gold-400 hover:text-gold-300 underline">
          Return home
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-forest-950 text-forest-100">
      <header className="border-b border-forest-800 bg-forest-900">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <a
            href="/"
            className="text-gold-400 hover:text-gold-300 text-sm font-medium"
          >
            ← Back to Lyrical Toolkit
          </a>
          <span className="text-forest-400 text-sm">{doc.title}</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {loading && (
          <p className="text-forest-300">Loading {doc.title}…</p>
        )}
        {error && (
          <div className="bg-red-900/30 border border-red-700 text-red-200 rounded-lg p-4">
            <p className="font-semibold mb-1">Couldn't load this document.</p>
            <p className="text-sm">{error}</p>
          </div>
        )}
        {!loading && !error && (
          <article className="legal-prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content}
            </ReactMarkdown>
          </article>
        )}
      </main>

      <footer className="border-t border-forest-800 bg-forest-900 mt-10">
        <div className="max-w-4xl mx-auto px-6 py-6 flex flex-wrap gap-4 text-sm text-forest-300">
          <a href="/privacy" className="hover:text-gold-400">Privacy</a>
          <a href="/terms" className="hover:text-gold-400">Terms</a>
          <a href="/subscription-terms" className="hover:text-gold-400">
            Subscription
          </a>
          <a href="/dmca" className="hover:text-gold-400">DMCA</a>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
